import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from 'react-native';

import { useResponsive } from '../hooks/useResponsive';
import { colors, fontSize, fontWeight, lineHeight, spacing } from '../theme';

/**
 * Step chrome: H1 and optional UOM row for Create Request / Request Detail (and aligned flows).
 * Prefer wrapping step bodies in `StepShell` so the outer column, UOM + title, and
 * `belowFormStepPageTitle` gap to the first card match across Opportunity, Site Walk, and Site information.
 * Spacing is driven from `theme/spacing`.
 */
export type StepHeaderVariant =
  | 'uom-then-title'
  | 'title-with-uom'
  | 'title-only';

export type StepHeaderProps = {
  title: string;
  variant: StepHeaderVariant;
  /** Renders immediately before the step H1 (e.g. web drill-in back to list). */
  titleLeading?: React.ReactNode;
  /** e.g. `<PermissionAwareField>…<UOMSelector … /></PermissionAwareField>`; placement depends on `variant` */
  uomControl?: React.ReactNode;
  /**
   * When `variant` is `uom-then-title`, optional controls on the same row as UOM (e.g. Basic/Advanced + PDF
   * on Request Detail). If omitted, UOM alone keeps the default top-right alignment.
   */
  uomRowEnd?: React.ReactNode;
  /** Renders after the heading block (e.g. offline callout) */
  afterTitle?: React.ReactNode;
  testID?: string;
};

const styles = StyleSheet.create({
  uomRowOnly: {
    alignSelf: 'flex-end',
    marginBottom: spacing.belowUomRow,
    maxWidth: 240,
  },
  uomRowWithEnd: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: spacing.belowUomRow,
    gap: spacing.fieldColumnGapWeb,
    flexWrap: 'nowrap',
  },
  uomRowWithEndCompact: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    flexWrap: 'nowrap',
    gap: spacing.belowUomRow,
    rowGap: spacing.belowUomRow,
  },
  uomRowEndOnly: {
    justifyContent: 'flex-end',
  },
  titleUomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: spacing.belowFormStepPageTitle,
    gap: spacing.fieldColumnGapWeb,
    flexWrap: 'wrap',
  },
  uomRowEndCluster: {
    flexShrink: 0,
    flexGrow: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'nowrap',
    gap: spacing.md,
    minWidth: 0,
  },
  uomRowEndClusterCompact: {
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
  },
  uomInlineCompact: {
    maxWidth: '100%',
  },
  uomInline: {
    maxWidth: 240,
    flexShrink: 0,
    minWidth: 0,
    alignSelf: 'center',
  },
  pageTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.belowFormStepPageTitle,
    gap: spacing.xs,
  },
  pageTitle: {
    fontSize: fontSize.formStepPageTitle,
    lineHeight: lineHeight.formStepPageTitle,
    fontWeight: fontWeight.bold,
    color: colors.primary,
    marginBottom: spacing.belowFormStepPageTitle,
  },
  pageTitleInRow: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
  },
  pageTitleInTitleUomRow: {
    flex: 1,
    minWidth: 120,
    marginBottom: 0,
  },
  titleWithUomCompactShell: {
    width: '100%',
  },
  titleActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: spacing.belowUomRow,
    gap: spacing.fieldColumnGapWeb,
    flexWrap: 'nowrap',
  },
  pageTitleInTitleActionsRow: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
  },
  pageTitleBeforeControlsRow: {
    marginBottom: spacing.belowUomRow,
  },
  titleWithUomChromeRow: {
    width: '100%',
    marginBottom: spacing.belowFormStepPageTitle,
    marginTop: 0,
  },
});

/**
 * Renders the step title (and optional UOM) with consistent design-system spacing.
 * Does not wrap subsection forms — only the top chrome above the first `StepForm` / card.
 */
function useCompactUomHeaderRow(): boolean {
  const { width } = useWindowDimensions();
  const { isSmallScreen } = useResponsive();
  if (Platform.OS !== 'web') {
    return true;
  }
  return isSmallScreen || width <= 640;
}

/** Web desktop: tail steps use UOM row then title; native keeps title-first compact chrome. */
export function resolveStepHeaderVariant(
  variant: StepHeaderVariant,
  isCompactUomRow: boolean,
): StepHeaderVariant {
  if (
    variant === 'title-with-uom' &&
    Platform.OS === 'web' &&
    !isCompactUomRow
  ) {
    return 'uom-then-title';
  }
  return variant;
}

function StepPageTitle({
  title,
  titleLeading,
  testID,
  style,
}: {
  title: string;
  titleLeading?: React.ReactNode;
  testID?: string;
  style?: object;
}) {
  if (titleLeading != null) {
    return (
      <View style={styles.pageTitleRow}>
        {titleLeading}
        <Text
          style={[styles.pageTitle, styles.pageTitleInRow, style]}
          accessibilityRole="header"
          testID={testID}
        >
          {title}
        </Text>
      </View>
    );
  }

  return (
    <Text
      style={[styles.pageTitle, style]}
      accessibilityRole="header"
      testID={testID}
    >
      {title}
    </Text>
  );
}

export const StepHeader: React.FC<StepHeaderProps> = ({
  title,
  variant,
  titleLeading,
  uomControl,
  uomRowEnd,
  afterTitle,
  testID,
}) => {
  const isCompactUomRow = useCompactUomHeaderRow();
  const resolvedVariant = resolveStepHeaderVariant(variant, isCompactUomRow);

  if (resolvedVariant === 'title-only') {
    return (
      <>
        <StepPageTitle
          title={title}
          titleLeading={titleLeading}
          testID={testID}
        />
        {afterTitle}
      </>
    );
  }

  if (resolvedVariant === 'title-with-uom') {
    const hasHeaderChrome = uomControl != null || uomRowEnd != null;

    if (isCompactUomRow && hasHeaderChrome) {
      return (
        <>
          <View style={styles.titleWithUomCompactShell}>
            <View
              style={styles.titleActionsRow}
              testID="step-title-actions-row"
            >
              <StepPageTitle
                title={title}
                titleLeading={titleLeading}
                testID={testID}
                style={styles.pageTitleInTitleActionsRow}
              />
              {uomRowEnd != null ? (
                <View style={styles.uomRowEndCluster}>{uomRowEnd}</View>
              ) : null}
            </View>
            {uomControl != null ? (
              <View
                style={[styles.uomRowWithEnd, styles.titleWithUomChromeRow]}
                testID="step-header-chrome-row"
              >
                <View style={styles.uomInlineCompact}>{uomControl}</View>
              </View>
            ) : null}
          </View>
          {afterTitle}
        </>
      );
    }

    return (
      <>
        <View style={styles.titleUomRow}>
          {titleLeading}
          <Text
            style={[styles.pageTitle, styles.pageTitleInTitleUomRow]}
            accessibilityRole="header"
            testID={testID}
          >
            {title}
          </Text>
          {uomControl != null ? (
            <View style={styles.uomInline}>{uomControl}</View>
          ) : null}
          {uomRowEnd != null ? (
            <View style={styles.uomRowEndCluster}>{uomRowEnd}</View>
          ) : null}
        </View>
        {afterTitle}
      </>
    );
  }

  return (
    <>
      {uomControl != null && uomRowEnd != null ? (
        <View
          style={[
            styles.uomRowWithEnd,
            isCompactUomRow && styles.uomRowWithEndCompact,
          ]}
        >
          <View
            style={[
              styles.uomInline,
              isCompactUomRow && styles.uomInlineCompact,
            ]}
          >
            {uomControl}
          </View>
          <View
            style={[
              styles.uomRowEndCluster,
              isCompactUomRow && styles.uomRowEndClusterCompact,
            ]}
          >
            {uomRowEnd}
          </View>
        </View>
      ) : uomControl != null ? (
        <View style={styles.uomRowOnly}>{uomControl}</View>
      ) : uomRowEnd != null ? (
        <View style={[styles.uomRowWithEnd, styles.uomRowEndOnly]}>
          <View style={styles.uomRowEndCluster}>{uomRowEnd}</View>
        </View>
      ) : null}
      <StepPageTitle
        title={title}
        titleLeading={titleLeading}
        testID={testID}
      />
      {afterTitle}
    </>
  );
};

StepHeader.displayName = 'StepHeader';
